import { getElementPosition } from './utils';

const tabsClass = 'c-tabs';
const tabClass = tabsClass + '__tab';
const tabActiveClass = tabClass + '--active';
const panelClass = tabsClass + '__panel';
const panelOpenClass = panelClass + '--open';

const tabsJsClass = 'js-tabs';
const tabJsClass = tabsJsClass + '-tab';
const panelJsClass = tabsJsClass + '-panel';

const tabsElements = document.querySelectorAll('.' + tabsJsClass) as NodeListOf<HTMLElement>;
const tabElements = [] as NodeListOf<HTMLElement>[];
const panelElements = [] as NodeListOf<HTMLElement>[];
const activeTabIndexes = [] as number[];

if (tabsElements.length > 0) {
    // We wait for the page to fully load
    window.addEventListener('load', () => {
        tabsElements.forEach((tabsElement, tabsIndex) => {
            // We get the tab elements
            tabElements.push(tabsElement.querySelectorAll('.' + tabJsClass));
            // We get the panel elements
            panelElements.push(tabsElement.querySelectorAll('.' + panelJsClass));
            // We set the initial index
            activeTabIndexes.push(0);

            tabElements[tabsIndex].forEach((tabElement, tabIndex) => {
                // If this is not the first tab
                if (tabIndex !== 0) {
                    // We change the tab index attribute
                    tabElement.tabIndex = -1;
                }

                // We add a click event listener on the tab
                tabElement.addEventListener('click', () => toggle(tabsIndex, tabIndex));

                // We add a keydown event listener on the tab
                tabElement.addEventListener('keydown', (event) => {
                    // If the left arrow key is pressed
                    if (
                        event.key === 'ArrowLeft'
                        && !event.ctrlKey
                        && !event.altKey
                        && !event.metaKey
                        && !event.shiftKey
                    ) {
                        // We toggle previous tab
                        previous(tabsIndex);
                    }

                    // If the right arrow key is pressed
                    if (
                        event.key === 'ArrowRight'
                        && !event.ctrlKey
                        && !event.altKey
                        && !event.metaKey
                        && !event.shiftKey
                    ) {
                        // We toggle the next tab
                        next(tabsIndex);
                    }
                });

                // We get the position of the tab in the page
                const tabPosition = getElementPosition(tabElement);

                // We create a unique id for the tab
                const tabId = `tab-${tabPosition.x}-${tabPosition.y}-${tabIndex}`;
                tabElement.id = tabId;

                // We add the aria-labelledby propery on the panel
                panelElements[tabsIndex][tabIndex].setAttribute('aria-labelledby', tabId);

                // We get the position of the panel in the page
                const panelPosition = getElementPosition(panelElements[tabsIndex][tabIndex]);

                // We create a unique id for the panel
                const panelId = `tab-panel-${panelPosition.x}-${panelPosition.y}-${tabIndex}`;
                panelElements[tabsIndex][tabIndex].id = panelId;

                // We add the aria-controls propery on the button
                tabElement.setAttribute('aria-controls', panelId);

                if (tabIndex === activeTabIndexes[tabsIndex]) {
                    // We add the active class on the tab
                    tabElement.classList.add(tabActiveClass);

                    // We add the open class on the panel
                    panelElements[tabsIndex][tabIndex].classList.add(panelOpenClass);
                }
            });
        });

    });
}

function toggle (tabsIndex: number, tabIndex: number): void {
    // If the tab is already active we stop here
    if (activeTabIndexes[tabsIndex] === tabIndex) {
        return;
    }

    // We store the previous active index
    const previousActiveIndex = activeTabIndexes[tabsIndex];

    // We update the active index
    activeTabIndexes[tabsIndex] = tabIndex;

    tabElements[tabsIndex].forEach((tabElement: HTMLElement, index: number) => {
        if (index === activeTabIndexes[tabsIndex]) {
            // We change the tab index attribute
            tabElement.tabIndex = 0;

            // We add the active class on the active tab
            tabElement.classList.add(tabActiveClass);

            // We focus the tab
            tabElement.focus();

            // We update the aria-selected property
            tabElement.setAttribute('aria-selected', 'true');

            // We add the open class on the panel
            panelElements[tabsIndex][index].classList.add(panelOpenClass);
        } else {
            // We change the tab index attribute
            tabElement.tabIndex = -1;

            // We remove the active class on the previous active tab
            tabElements[tabsIndex][previousActiveIndex].classList.remove(tabActiveClass);

            // We update the aria-selected property
            tabElements[tabsIndex][previousActiveIndex].setAttribute('aria-selected', 'false');

            // We remove the open class on the previous panel panels
            panelElements[tabsIndex][previousActiveIndex].classList.remove(panelOpenClass);
        }
    });
}

function previous (tabsIndex: number): void {
    // If there is a previous tab
    if (activeTabIndexes[tabsIndex] > 0) {
        // We toggle it
        toggle(tabsIndex, activeTabIndexes[tabsIndex] - 1);
    } else {
        // Otherwise we toggle the last tab
        toggle(tabsIndex, tabElements[tabsIndex].length - 1);
    }
}

function next (tabsIndex: number): void {
    // If there is a next tab
    if (activeTabIndexes[tabsIndex] < tabElements[tabsIndex].length - 1) {
        // We toggle it
        toggle(tabsIndex, activeTabIndexes[tabsIndex] + 1);
    } else {
        // Otherwise we toggle the first tab
        toggle(tabsIndex, 0);
    }
}